import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { RootState } from '../../../reducers';
import * as actions from '../../../actions/generalActions';
import * as adminActions from '../../../actions/admin';
import * as productActions from '../../../actions/production';
import * as transportActions from '../../../actions/transport';
import { SelectOption } from '../../../types/selectOption.type';
import ArticlePageHeader from './components/ArticlePageHeader';
import ArticleForm, { ArticleFormState, DEFAULT_FORM_STATE, BomMaterial, KubitRawMaterialOption, KubitBomRow } from './ArticleForm';
import { ChipOption } from './components/ChipMultiSelect';
import { axiosCaller } from '../../../utils/apiCaller';
import './articles.scss';

interface Props {
    history: any;
    match: any;
    actions: any;
    adminActions: any;
    productActions: any;
    transportActions: any;
    units: SelectOption[];
    departments: SelectOption[];
    admin: any;
    transport: any;
    production: any;
    companyProfile?: any;
    buttonLoader: boolean;
}

const toChipOptions = (list: any[]): ChipOption[] =>
    (list || []).map((item) => ({
        value: item.value ?? item.id,
        label: item.label ?? item.name ?? item.supplier ?? String(item.value ?? item.id),
    }));

// MaterialResource carries the BOM pivot (quantity + unitId) for an article's
// materials; preserve them so the recipe rehydrates with its amounts/units.
const toBomMaterials = (list: any[]): BomMaterial[] =>
    (list || []).map((item) => ({
        value: item.value ?? item.id,
        label: item.label ?? item.name ?? String(item.value ?? item.id),
        quantity: item.quantity != null ? Number(item.quantity) : 1,
        unitId: item.unitId ?? item.unit_id ?? null,
    }));

// KuBIT raw-material options carry their consume responsibilities (for the
// "no consume step" warning). Normalize {value|id, label|name, consumeResponsibilities}.
const normalizeKubitRawMaterials = (list: any[]): KubitRawMaterialOption[] =>
    (list || []).map((r) => ({
        value: r.value ?? r.id,
        label: r.label ?? r.name ?? String(r.value ?? r.id),
        unitId: r.unitId ?? r.unit?.id ?? null,
        unitLabel: r.unit?.name ?? r.unit?.label ?? undefined,
        consumeResponsibilities: toChipOptions(r.consumeResponsibilities),
    }));

// Hydrate saved BOM rows: the article resource carries {id,name,quantity,unit,
// consumeResponsibilities} per component — preserve the quantity + unit + consume
// responsibilities so the table and the "no consume step" warning work offline.
const toKubitBomRows = (list: any[]): KubitBomRow[] =>
    (list || []).map((r) => ({
        value: r.value ?? r.id,
        label: r.label ?? r.name ?? String(r.value ?? r.id),
        quantity: r.quantity != null ? Number(r.quantity) : 1,
        unitId: r.unitId ?? r.unit?.id ?? null,
        unitLabel: r.unit?.name ?? r.unit?.label ?? undefined,
        consumeResponsibilities: toChipOptions(r.consumeResponsibilities),
    }));

function mapArticleToFormState(article: any): ArticleFormState {
    if (!article) return { ...DEFAULT_FORM_STATE };

    const unitId = article.unit?.id ?? article.unit?.value ?? null;
    const departmentId = article.department?.id ?? article.department?.value ?? null;
    const firstSupplier = (article.suppliers || [])[0];
    const supplierId = firstSupplier?.id ?? firstSupplier?.value ?? null;
    const supplierCode = (article.code || [])[0]?.supplierCode || '';

    return {
        name: article.name || '',
        // productCode may arrive as a number from the API for codes that look
        // numeric (axios JSON-parses "123" → 123). Force to string — the input
        // and validate() both assume string.
        productCode: article.productCode != null ? String(article.productCode) : '',
        forProduction: article.forProduction ? 1 : 0,
        kubitItemTypeId: article.kubitItemTypeId ?? null,
        kubitTaxId: article.kubitTaxId ?? null,
        kubitConsumeResponsibilities: toChipOptions(article.kubitConsumeResponsibilities),
        // Preserve each component's quantity + unit + consumeResponsibilities so the
        // BOM table and the "no consume step" warning work from hydrated data alone,
        // without depending on the separate /kubit-raw-materials fetch resolving.
        kubitComponents: toKubitBomRows(article.kubitComponents),
        pricePerUnit: article.pricePerUnit ?? '',
        costPerUnit: article.costPerUnit ?? '',
        unitId,
        image: null,
        imagePreview: article.image || null,
        description: article.description || '',
        departmentId,
        dailyCapacity: article.dailyCapacity ?? '',
        extraTime: article.extraTime ?? '',
        materials: toBomMaterials(article.material),
        equipments: toChipOptions(article.equipments),
        supplierId,
        supplierCode,
        deliveryDays: article.deliveryDays || '',
        responsibilities: toChipOptions(article.responsibilities),
    };
}

const ArticleEditPage = (props: Props) => {
    const articleId = Number(props.match.params.id);
    const [articleFetched, setArticleFetched] = useState(false);
    const [kubitRawMaterials, setKubitRawMaterials] = useState<KubitRawMaterialOption[]>([]);

    // KuBIT-only: raw-material (type-3) articles selectable as this article's
    // KuBIT bill of materials. Fetched only for KuBIT tenants.
    useEffect(() => {
        if (!props.companyProfile?.kubitEnabled) return;
        axiosCaller('GET', 'articles/kubit-raw-materials')
            .then((res: any) => setKubitRawMaterials(normalizeKubitRawMaterials(res?.data?.data ?? res?.data)))
            .catch(() => undefined);
    }, [props.companyProfile?.kubitEnabled]);

    useEffect(() => {
        // Reset any stale article state from a previous edit page mount.
        props.adminActions.resetArticle();

        // Fetch all option lists in parallel.
        props.actions.fetchUnits();
        props.actions.getAllDepartments();
        props.adminActions.fetchMaterials();
        props.adminActions.getSuppliers();
        props.transportActions.fetchEquipments();
        props.productActions.fetchResponsibilities();

        // Fetch the article itself.
        Promise.resolve(props.adminActions.fetchArticle(articleId))
            .catch(() => undefined)
            .finally(() => setArticleFetched(true));

        return () => {
            props.adminActions.resetArticle();
        };
    }, [articleId]);

    const articleInformation = props.admin?.articleInformation;
    const articleHydrated = articleInformation && Object.keys(articleInformation).length > 0;

    const suppliers: SelectOption[] = useMemo(
        () => (props.admin?.suppliers || []).map((s: any) => ({ value: s.id, label: s.supplier })),
        [props.admin?.suppliers],
    );
    // UnitResource returns {id, name}; normalize to {value, label} for the form.
    const units: SelectOption[] = useMemo(
        () => (props.units || []).map((u: any) => ({ value: u.value ?? u.id, label: u.label ?? u.name })),
        [props.units],
    );
    const materials: ChipOption[] = useMemo(() => toChipOptions(props.admin?.materials), [props.admin?.materials]);
    const equipments: ChipOption[] = useMemo(() => toChipOptions(props.transport?.equipments), [props.transport?.equipments]);
    const responsibilities: ChipOption[] = useMemo(
        () => toChipOptions(props.production?.responsibilities),
        [props.production?.responsibilities],
    );

    const initialValues = useMemo(() => mapArticleToFormState(articleInformation), [articleInformation]);

    const goList = () => props.history.push('/admin/articles');

    const buildFormData = (form: ArticleFormState): FormData => {
        const fd = new FormData();
        fd.append('name', form.name);
        fd.append('productCode', form.productCode);
        fd.append('forProduction', String(form.forProduction));
        fd.append('description', form.description || '');
        fd.append('pricePerUnit', form.pricePerUnit !== '' ? String(form.pricePerUnit) : '');
        fd.append('costPerUnit', form.costPerUnit !== '' ? String(form.costPerUnit) : '');
        if (form.unitId != null) fd.append('unitId', String(form.unitId));
        if (form.kubitItemTypeId != null) fd.append('kubitItemTypeId', String(form.kubitItemTypeId));
        // != null so 0 (0% VAT) is appended, not dropped as falsy.
        if (form.kubitTaxId != null) fd.append('kubitTaxId', String(form.kubitTaxId));
        form.kubitConsumeResponsibilities.forEach((r) => fd.append('kubitConsumeResponsibilities[]', String(r.value)));
        // KuBIT bill of materials: raw-material component article id + template
        // quantity. Backend scopes/clears it, so it round-trips on removal too.
        form.kubitComponents.forEach((c, i) => {
            fd.append(`kubitComponents[${i}][id]`, String(c.value));
            fd.append(`kubitComponents[${i}][quantity]`, String(c.quantity === '' ? 1 : c.quantity));
        });
        if (form.image) fd.append('image', form.image);

        if (form.forProduction === 1) {
            if (form.departmentId != null) fd.append('departmentId', String(form.departmentId));
            if (form.dailyCapacity !== '') fd.append('dailyCapacity', String(form.dailyCapacity));
            fd.append('extraTime', String(form.extraTime === '' ? 0 : form.extraTime));
            form.materials.forEach((m, i) => {
                fd.append(`materials[${i}][materialId]`, String(m.value));
                fd.append(`materials[${i}][quantity]`, String(m.quantity === '' ? 1 : m.quantity));
                if (m.unitId != null) fd.append(`materials[${i}][unitId]`, String(m.unitId));
            });
            form.equipments.forEach((e) => fd.append('equipments[]', String(e.value)));
            form.responsibilities.forEach((r) => fd.append('responsibilities[]', String(r.value)));
        } else {
            if (form.supplierId != null) fd.append('supplier[]', String(form.supplierId));
            fd.append('suppliersCode[0][code]', form.supplierCode || '');
            fd.append('deliveryDays', form.deliveryDays || '');
        }

        return fd;
    };

    const handleSubmit = (form: ArticleFormState) => {
        const fd = buildFormData(form);
        return props.adminActions.editArticle(articleId, fd).then((ok: any) => {
            if (ok !== false) goList();
        });
    };

    const title = articleHydrated
        ? articleInformation.name
        : <FormattedMessage id="admin.articles.edit.title" defaultMessage="Edito artikullin" />;

    return (
        <div className="art-page">
            <ArticlePageHeader
                onBack={goList}
                title={title}
            />

            {!articleFetched ? (
                <div className="art-edit-loading">
                    <FormattedMessage id="admin.articles.edit.loading" defaultMessage="Duke ngarkuar artikullin..." />
                </div>
            ) : !articleHydrated ? (
                <div className="art-empty">
                    <h3 className="art-empty__title">
                        <FormattedMessage id="admin.articles.edit.notFound.title" defaultMessage="Artikulli nuk u gjet" />
                    </h3>
                    <p className="art-empty__subtitle">
                        <FormattedMessage
                            id="admin.articles.edit.notFound.subtitle"
                            defaultMessage="Mund të jetë fshirë ose lidhja është e gabuar."
                        />
                    </p>
                    <button type="button" className="art-btn art-btn--ghost" onClick={goList}>
                        <FormattedMessage id="admin.articles.stub.backToList" defaultMessage="Kthehu te lista" />
                    </button>
                </div>
            ) : (
                <ArticleForm
                    key={articleId}
                    mode="edit"
                    initialValues={initialValues}
                    isLoading={!!props.buttonLoader}
                    kubitEnabled={!!props.companyProfile?.kubitEnabled}
                    units={units}
                    departments={props.departments || []}
                    suppliers={suppliers}
                    materials={materials}
                    equipments={equipments}
                    responsibilities={responsibilities}
                    kubitRawMaterials={kubitRawMaterials}
                    onSubmit={handleSubmit}
                    onCancel={goList}
                />
            )}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        admin: state.admin,
        transport: state.transport,
        production: state.production,
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch),
        productActions: bindActionCreators(productActions as any, dispatch),
        transportActions: bindActionCreators(transportActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ArticleEditPage as any));
